import UnpairedIndicatorIcon from '@op/assets/unpaired-indicator.svg'
import type { IntendedBlockState } from '@op/services'
import cls from 'classnames'
import type { CSSProperties } from 'react'
import { memo } from 'react'

type DeviceStatusIndicatorProps = {
	readonly className?: string
	readonly iconClassName?: string
	readonly intendedState: Maybe<IntendedBlockState>
	readonly style?: CSSProperties
}

export const DeviceStatusIndicator = memo(
	({ intendedState, className, iconClassName, style }: DeviceStatusIndicatorProps) => {
		if (intendedState == null) return null

		if (intendedState == 'not_paired') {
			return (
				<img
					src={UnpairedIndicatorIcon}
					alt="warning"
					aria-hidden="true"
					className={cls('h-6 w-6 lg:h-7 lg:w-7 z-10', iconClassName)}
					style={{
						...style,
						bottom: normalizeIconOffset(style?.bottom),
						height: normalizeIconSize(style?.height),
						right: normalizeIconOffset(style?.right),
						width: normalizeIconSize(style?.width),
					}}
				/>
			)
		}

		const statusColorClass = (
			{
				allowed: 'bg-green',
				blocked: 'bg-red',
				paused: 'bg-gray-70',
			} satisfies Record<Exclude<IntendedBlockState, 'not_paired'>, string>
		)[intendedState]

		return (
			<div
				className={cls(
					'flex-center h-5 w-5 lg:h-6 lg:w-6 z-10 rounded-full',
					statusColorClass,
					className,
				)}
				style={style}
			/>
		)
	},
)

DeviceStatusIndicator.displayName = 'DeviceStatusIndicator'

const ICON_SIZE_MODIFIER = 3
const ICON_OFFSET_MODIFIER = 1.5

const normalizeIconSize = (size: number | string | undefined) =>
	Number.isFinite(size) ? size + ICON_SIZE_MODIFIER : size

const normalizeIconOffset = (offset: number | string | undefined) =>
	Number.isFinite(offset) ? offset - ICON_OFFSET_MODIFIER : offset
